import { TodoStatus } from "@acto/gql/graphql";
import type { User } from "@acto/taskpane/core/domain";
import { useUpdateTodos } from "@acto/taskpane/core/data-access/todos/useUpdateTodos";
import { toIsoDate } from "@acto/taskpane/field-sales-2/components/primitives/Calendar";

/**
 * The editable fields of a recap todo. `dueDate` is optional (a todo may have no due date); when
 * set it is a UTC-midnight `Date` (the DatePicker's representation), serialized with `toIsoDate()`
 * to the backend's `YYYY-MM-DD` contract. When unset it is persisted as `null` (cleared).
 */
export interface RecapTodoEdit {
  description: string;
  dueDate?: Date;
  /** When set, reassigns the todo to this person. Carries the full assignee (not just the id) so
   *  callers can render the change optimistically. */
  assignee?: User;
}

export interface UseEditRecapTodoResult {
  /** Persist edited description + due date via `updateTodo`. Resolves `true` on success. */
  saveTodo: (id: string, edit: RecapTodoEdit) => Promise<boolean>;
  /** Soft-delete via `updateTodo` with status DELETED. Resolves `true` on success. */
  deleteTodo: (id: string) => Promise<boolean>;
}

/**
 * Edit/delete surface for recap (note-keyed) todos.
 *
 * Editing and deletion both go through the `updateTodo` mutation: an edit writes
 * `description` + `dueDate`; a deletion sets `processedStatus` to DELETED (soft-delete). Recap
 * todos surfaced in the edit screen are OPEN, so an edit keeps that status. Callers apply the
 * change optimistically; the `noteTodoChanged` subscription reconciles server state on success,
 * and the returned `false` lets callers roll the optimistic change back on failure.
 */
export function useEditRecapTodo(): UseEditRecapTodoResult {
  const { updateTodos } = useUpdateTodos();

  const saveTodo = async (id: string, edit: RecapTodoEdit) => {
    const updated = await updateTodos({
      input: {
        id,
        processedStatus: TodoStatus.Open,
        description: edit.description,
        dueDate: edit.dueDate ? toIsoDate(edit.dueDate) : null,
        assignedUserId: edit.assignee?.id,
      },
    });
    return Boolean(updated);
  };

  const deleteTodo = async (id: string) => {
    const updated = await updateTodos({ input: { id, processedStatus: TodoStatus.Deleted } });
    return Boolean(updated);
  };

  return { saveTodo, deleteTodo };
}
